'use client'

import { useState } from 'react'

import { useForm } from 'react-hook-form'

import { DateRangeInput } from '~/app/accounting/filters/DateRangeInput'
import { Period } from '~/app/accounting/period-utils'
import {
  availableReports,
  buildReportParams,
  reportEntry,
} from '~/app/accounting/reports/report-catalog'
import {
  getReportPeriods,
  useReportPeriods,
} from '~/app/accounting/reports/report-periods'
import {
  AccountingReportKind,
  ReportFormat,
  useGenerateReport,
} from '~/app/accounting/useGenerateReport'
import { useImportedAccounts } from '~/app/accounting/useImportedAccounts'
import {
  CheckboxGroupInput,
  Group,
  RadioGroupInput,
  SegmentedControl,
  SelectInput,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { endOfDay, reinterpretYmdAcrossTz } from '~/utils/timezone'

type NewReportFormValues = {
  kind: AccountingReportKind
  accountIds: string[]
  accountScope: 'all' | 'custom'
  format: ReportFormat
}

const FORMATS: ReportFormat[] = ['csv', 'pdf']
const FORMAT_LABELS: Record<ReportFormat, string> = {
  csv: 'CSV',
  pdf: 'PDF',
}

// Name over description, both on one line each, so the picker is the same
// height for every report and the descriptions are readable while choosing
// rather than only after.
const ReportOption = ({
  label,
  description,
}: {
  label: string
  description: string
}) => (
  <div className="flex min-w-0 flex-col text-left">
    <span className="truncate">{label}</span>
    <span
      data-select-option-only
      className="hidden truncate text-xs text-secondary"
    >
      {description}
    </span>
  </div>
)

/**
 * Builds one report. Every period preset is a pre-filled version of this same
 * form, so there is no separate custom path to keep in step.
 */
export const NewReportDialog = () => {
  const { closeGlobalModal } = useAuth()
  const { featureFlags } = useActiveOrg()
  const { smartAccounts } = useSmartAccounts()
  const { importedAccounts } = useImportedAccounts()
  const [committedTz] = useAccountingTimezone()
  const [tz, setTz] = useState(committedTz)
  const periods = useReportPeriods(tz)
  const [period, setPeriod] = useState<Period>(periods[0])
  const reports = availableReports(!!featureFlags?.['accounting-v2'])

  const { control, watch, setValue } = useForm<NewReportFormValues>({
    mode: 'onChange',
    defaultValues: {
      kind: reports[0].kind,
      accountIds: [],
      accountScope: 'all',
      format: reports[0].defaultFormat,
    },
  })

  const values = watch()
  const entry = reportEntry(values.kind) ?? reports[0]

  const { isGenerating, generate } = useGenerateReport({
    kind: entry.kind,
    filterParams: buildReportParams({
      entry,
      startDate: period.startDate,
      endDate: period.endDate,
      accountIds: values.accountIds ?? [],
    }),
    // The report lands in the list behind this dialog rather than the
    // browser's downloads.
    autoDownload: false,
  })

  // Format follows the report rather than sticking, so picking a statement and
  // then a CSV-shaped report doesn't leave you on PDF.
  const selectKind = (kind: AccountingReportKind) => {
    setValue('kind', kind)
    setValue('format', reportEntry(kind)?.defaultFormat ?? 'csv')
  }

  // An empty custom selection would omit accountIds entirely, which the report
  // endpoints read as every account.
  const canSubmit =
    !!period.startDate &&
    !!period.endDate &&
    (values.accountScope === 'all' || values.accountIds.length > 0)

  const toggleTz = () => {
    const newTz = tz === 'utc' ? 'local' : 'utc'
    setTz(newTz)
    const matchingPreset = getReportPeriods(newTz).find(
      (preset) => preset.label === period.label,
    )
    if (matchingPreset) {
      setPeriod(matchingPreset)
      return
    }
    const startDate = reinterpretYmdAcrossTz(period.startDate, tz, newTz)
    const endMidnight = reinterpretYmdAcrossTz(period.endDate, tz, newTz)
    const rawEndDate = endMidnight ? endOfDay(endMidnight, newTz) : undefined
    const now = new Date()
    setPeriod({
      ...period,
      startDate,
      endDate:
        rawEndDate && rawEndDate.getTime() > now.getTime() ? now : rawEndDate,
    })
  }

  const submit = async () => {
    await generate(values.format)
    closeGlobalModal()
  }

  return (
    <SplitsDialogContent
      id="newAccountingReport"
      variant="cta"
      title="New report"
      isLoading={isGenerating}
      actionButton={{
        text: 'Generate',
        onClick: submit,
        isDisabled: !canSubmit,
      }}
      cancelButton={{ text: 'Cancel' }}
    >
      <div className="flex flex-col gap-4">
        <SelectInput
          label="Report"
          placeholder="Select a report"
          options={reports.map((report) => ({
            value: report.kind,
            label: (
              <ReportOption
                label={report.label}
                description={report.description}
              />
            ),
          }))}
          selectedOption={values.kind}
          selectValue={selectKind}
        />

        <DateRangeInput
          period={period}
          presets={periods}
          setPeriod={setPeriod}
          size="base"
          tz={tz}
          onToggleTz={toggleTz}
        />

        {/* Both formats always show, with the unsupported one disabled, so the
            form doesn't reflow as you change reports and it's clear that a
            statement only comes as a PDF. */}
        <Group label="Format">
          <SegmentedControl
            control={control}
            inputName="format"
            ariaLabel="Report format"
            options={FORMATS.map((format) => ({
              value: format,
              label: FORMAT_LABELS[format],
              isDisabled: !entry.formats.includes(format),
            }))}
          />
        </Group>

        <div className="flex flex-col">
          <RadioGroupInput
            label="Accounts"
            size="small"
            options={[
              { value: 'all', label: 'All' },
              { value: 'custom', label: 'Custom' },
            ]}
            value={values.accountScope}
            onChange={(scope) => {
              setValue('accountScope', scope as 'all' | 'custom')
              if (scope === 'all') setValue('accountIds', [])
            }}
          />

          {values.accountScope === 'custom' && (
            <div className="-mt-1 pl-6">
              <CheckboxGroupInput
                size="small"
                options={[
                  ...smartAccounts.map((smartAccount) => ({
                    value: smartAccount.id,
                    label: smartAccount.name,
                  })),
                  // "All" leaves imported accounts out, so this is the only
                  // way to report on them.
                  ...importedAccounts.map((account) => ({
                    value: account.id,
                    label: `${account.name} (imported)`,
                  })),
                ]}
                control={control}
                inputName="accountIds"
              />
            </div>
          )}
        </div>
      </div>
    </SplitsDialogContent>
  )
}
